import { useState, useEffect } from 'react';
import { FiPlay, FiBook, FiGrid, FiCpu, FiZap, FiArrowRight, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../utils/api';
import StatusIndicator from '../common/StatusIndicator';

interface AvailableInstance {
  instance_id: string;
  provider: string;
  instance_type: string;
  region: string;
  gpu_type: string;
  hourly_cost: number;
  has_notebook: boolean;
  project_name: string;
  status: string;
}

interface QuickStartProps {
  onDismiss?: () => void;
  /** Compact mode for sidebar or smaller spaces */
  compact?: boolean;
}

export default function QuickStart({ onDismiss, compact = false }: QuickStartProps) {
  const navigate = useNavigate();
  const [instances, setInstances] = useState<AvailableInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    fetchInstances();
  }, []);

  const fetchInstances = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/notebooks/available-instances`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      if (response.ok) {
        const data = await response.json();
        setInstances(data);
      }
    } catch (err) {
      console.error('Error fetching instances:', err);
    } finally {
      setLoading(false);
    }
  };

  const runningInstances = instances.filter(i => i.status === 'running');
  const idleInstances = runningInstances.filter(i => !i.has_notebook);
  const hasReadyInstance = idleInstances.length > 0;

  const handleQuickLaunch = async () => {
    if (idleInstances.length > 0) {
      // Launch notebook on first idle instance
      setLaunching(true);
      const instance = idleInstances[0];
      sessionStorage.setItem('notebook_launch_params', JSON.stringify({
        instance_id: instance.instance_id
      }));
      navigate('/dashboard/notebook/new-session');
    } else if (runningInstances.length > 0) {
      // Has running instances but all have notebooks - go to notebooks page
      navigate('/dashboard/notebooks');
    } else {
      // No instances - go to compute to launch one
      navigate('/dashboard/compute');
    }
  };

  const templates = [
    {
      icon: FiPlay,
      title: 'Launch Notebook',
      description: hasReadyInstance
        ? 'Start coding on available GPU'
        : 'Launch Jupyter on GPU compute',
      action: handleQuickLaunch,
      primary: true,
      badge: hasReadyInstance ? `${idleInstances.length} ready` : null,
      useCase: 'Run code now'
    },
    {
      icon: FiGrid,
      title: 'Browse Templates',
      description: 'Pre-built strategy starting points',
      action: () => navigate('/dashboard/strategy-library'),
      primary: false,
      useCase: 'Starting point'
    },
    {
      icon: FiCpu,
      title: 'Explore GPUs',
      description: 'Browse instance types & pricing',
      action: () => navigate('/dashboard/compute'),
      primary: false,
      useCase: 'Research'
    },
    {
      icon: FiBook,
      title: 'Sample Notebooks',
      description: 'Pre-built trading strategies',
      action: () => navigate('/dashboard/notebooks'),
      primary: false,
      useCase: 'Learning'
    }
  ];

  // Compact mode for embedding in sidebar or small spaces
  if (compact) {
    return (
      <div className="space-y-2">
        <button
          onClick={handleQuickLaunch}
          disabled={launching}
          className="w-full flex items-center gap-3 p-3 bg-white text-black rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          {launching ? (
            <FiLoader className="w-4 h-4 animate-spin" />
          ) : (
            <FiZap className="w-4 h-4" />
          )}
          <span className="font-medium">
            {launching ? 'Launching...' : hasReadyInstance ? 'Run Notebook Now' : 'Launch Notebook'}
          </span>
          {hasReadyInstance && !launching && (
            <span className="ml-auto text-xs bg-black/10 px-2 py-0.5 rounded-full">
              {idleInstances.length} ready
            </span>
          )}
        </button>

        {/* Instance status indicator */}
        {!loading && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400">
            <StatusIndicator
              status={runningInstances.length > 0 ? 'ready' : 'idle'}
              size="sm"
            />
            <span>
              {runningInstances.length > 0
                ? `${runningInstances.length} instance${runningInstances.length !== 1 ? 's' : ''} running`
                : 'No instances running'
              }
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-white mb-1">Get Started</h2>
          <p className="text-sm text-gray-400">
            Choose how you want to begin
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Instance availability badge */}
          {!loading && (
            <div className="flex items-center gap-2 text-sm">
              <StatusIndicator
                status={runningInstances.length > 0 ? 'ready' : 'idle'}
                size="sm"
                pulse={runningInstances.length > 0}
              />
              <span className={runningInstances.length > 0 ? 'text-profit' : 'text-gray-500'}>
                {runningInstances.length > 0
                  ? `${runningInstances.length} running`
                  : 'No instances'
                }
              </span>
            </div>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-gray-500 hover:text-gray-300 text-sm"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* Primary action - one-click notebook launch */}
      {hasReadyInstance && (
        <button
          onClick={handleQuickLaunch}
          disabled={launching}
          className="w-full mb-4 p-4 bg-white text-black rounded-md hover:bg-gray-100 transition-colors flex items-center justify-between disabled:opacity-50"
        >
          <div className="flex items-center gap-3">
            {launching ? (
              <FiLoader className="w-5 h-5 animate-spin" />
            ) : (
              <FiZap className="w-5 h-5" />
            )}
            <div className="text-left">
              <h3 className="font-medium">
                {launching ? 'Launching notebook...' : 'Run Notebook Now'}
              </h3>
              <p className="text-sm text-gray-600">
                {idleInstances[0]?.gpu_type} • ${(idleInstances[0]?.hourly_cost ?? 0).toFixed(2)}/hr
              </p>
            </div>
          </div>
          <FiArrowRight className="w-5 h-5" />
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {templates.map((template, index) => (
          <button
            key={index}
            onClick={template.action}
            disabled={template.primary && launching}
            className={`text-left p-4 rounded-md border transition-colors disabled:opacity-50 ${
              template.primary && !hasReadyInstance
                ? 'bg-white text-black border-white hover:bg-gray-100'
                : 'bg-gray-800 text-white border-gray-700 hover:border-gray-600 hover:bg-gray-750'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <template.icon className={`w-5 h-5 ${
                template.primary && !hasReadyInstance ? 'text-gray-800' : 'text-gray-400'
              }`} />
              {template.badge && (
                <span className="text-xs bg-profit/20 text-profit px-2 py-0.5 rounded-full">
                  {template.badge}
                </span>
              )}
            </div>
            <h3 className="font-medium mb-1">{template.title}</h3>
            <p className={`text-sm ${
              template.primary && !hasReadyInstance ? 'text-gray-600' : 'text-gray-500'
            }`}>
              {template.description}
            </p>
            <p className={`text-xs mt-2 ${
              template.primary && !hasReadyInstance ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {template.useCase}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
